/**
 * Shared body for the "widget lost its integration" page, used by both
 * AttachAlertIntegrationPage.js and AttachIntegrationPage.js — previously
 * two ~200-line, ~95%-duplicated pages differing only by entity noun and
 * store hook. Stays a separate route from the actual builder (AlertPage.js/
 * CalendarPage.js), which is already heavy and shouldn't have to know about
 * the "no integration" case at all — the visual match with BuilderIdentityBand
 * just avoids a jump when the caller navigates into the builder after a
 * successful attach.
 * Spec: assets/handoffs/design_handoff_attach_integration
 *
 * @param {'alert'|'calendar'} entityNoun - drives card copy
 * @param {string} indexHref - "/alerts" | "/calendars"
 * @param {string} indexLabel - "Event alerts" | "Calendars"
 * @param {object} widgetModel - {uuid, name, integration_uuid}
 * @param {boolean} untitled
 * @param {boolean} published
 * @param {function} onUpdateField - (field, value) => void
 * @param {object} formErrors
 * @param {Array} integrations - connected integrations, already fetched by the caller
 * @param {boolean} attaching
 * @param {*} attachError
 * @param {function} onAttach - () => void
 * @param {function} onDelete - wired to the band's "Delete widget"
 */
import React, { useCallback } from "react";
import PageTemplate from "../PageTemplate";
import BuilderIdentityBand from "../../component/WidgetSettings/BuilderIdentityBand/BuilderIdentityBand";
import ApiError from "../../component/ApiError";
import Icon from "../../theme/untitled/icons/Icon";
import Input from "../../theme/untitled/Input/Input";
import Button from "../../theme/untitled/Button/Button";
import IntegrationAttachSelect from "./IntegrationAttachSelect";
import styles from "./AttachIntegrationTemplate.module.scss";

const noop = () => {};

const AttachIntegrationTemplate = ({
  entityNoun,
  indexHref,
  indexLabel,
  widgetModel,
  untitled,
  published,
  onUpdateField,
  formErrors,
  integrations,
  attaching,
  attachError,
  onAttach,
  onDelete,
}) => {
  const name = untitled
    ? `Untitled ${entityNoun}`
    : widgetModel.name || `Untitled ${entityNoun}`;
  const attachLabel = attaching ? "Attaching…" : "Attach integration";
  const sidebarId = entityNoun === "alert" ? "alerts" : "calendar";

  const onNameChange = useCallback(
    (e) => onUpdateField("name", e.target.value),
    [onUpdateField]
  );
  const onIntegrationChange = useCallback(
    (uuid) => onUpdateField("integration_uuid", uuid),
    [onUpdateField]
  );

  return (
    <PageTemplate
      sidebarId={sidebarId}
      className="page-wrapper-widget"
      header={
        <BuilderIdentityBand
          name={name}
          untitled={untitled}
          published={published}
          indexHref={indexHref}
          indexLabel={indexLabel}
          locked
          onPreview={noop}
          onDelete={onDelete}
          panelHidden
          onTogglePanel={noop}
          settingsOpen={false}
          onToggleSettings={noop}
        />
      }
    >
      <div className={styles.builderRow}>
        <div className={styles.canvas}>
          <div className={styles.cardWrap}>
            <div className={styles.card}>
              <div className={styles.header}>
                <span className={styles.featuredIcon}>
                  <Icon
                    name="AlertTriangle"
                    size={20}
                    className={styles.featuredIconGlyph}
                  />
                </span>
                <div>
                  <h2 className={styles.title}>
                    Attach an integration to restore this widget
                  </h2>
                  <p className={styles.supporting}>
                    This {entityNoun}&rsquo;s integration was disconnected, so
                    it has no data source. Wherever it&rsquo;s shared or
                    embedded it isn&rsquo;t working, and editing is locked until
                    you attach one.
                  </p>
                </div>
              </div>

              <div className={styles.body}>
                <Input
                  label="Widget name"
                  value={widgetModel.name || ""}
                  onChange={onNameChange}
                  disabled={attaching}
                  error={formErrors.nameError}
                  required
                />

                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Integration</label>
                  <IntegrationAttachSelect
                    integrations={integrations}
                    value={widgetModel.integration_uuid}
                    onChange={onIntegrationChange}
                    disabled={attaching}
                    error={formErrors.integrationError}
                  />
                </div>

                <div className={styles.note} role="note">
                  <Icon
                    name="AlertCircle"
                    size={20}
                    className={styles.noteIcon}
                  />
                  <div>
                    <p className={styles.noteTitle}>
                      About your filters settings
                    </p>
                    <ul className={styles.noteList}>
                      <li>
                        Attach the same account as before and they&rsquo;re
                        kept.
                      </li>
                      <li>
                        Attach a different account or integration and
                        they&rsquo;re reset to defaults.
                      </li>
                    </ul>
                  </div>
                </div>

                <ApiError
                  error={attachError}
                  heading="Couldn’t attach the integration"
                  defaultErrorMessage="Nothing was changed. Check it’s still connected on the Integrations page, then try again."
                  className={styles.failedAlert}
                />
              </div>

              <div className={styles.footer}>
                <Button
                  hierarchy="primary"
                  disabled={!widgetModel.integration_uuid || attaching}
                  loading={attaching}
                  onClick={onAttach}
                >
                  {attachLabel}
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.sidebarSpacer} />
      </div>
    </PageTemplate>
  );
};

export default AttachIntegrationTemplate;
